import { BLOTTER_ID_OPEN_ORDERS } from '@src/containers/Blotters/Orders/tokens';
import {
  BlotterDensity,
  MobileDataTable,
  useAccordionFilterBuilder,
  useBlotterTable,
  usePersistedBlotterTable,
  type Order,
} from '@talos/kyoko';
import { useCallback } from 'react';
import { useOrderColumns } from '../../../../Blotters/Orders/Columns';
import { useOpenOrdersBlotterObs } from '../../../../Blotters/Orders/useOpenOrdersBlotterObs';
import { useOrderFilter, type BlotterTableOrderFilter } from '../../../../Blotters/Orders/useOrderFilter';
import { usePredictionMarketBlotterColumns } from '../../../../Blotters/predictionMarketBlotter';
import { MobileBlotterToolbar } from '../MobileBlotterToolbar';
import { MobileOrderCard, useMobileOrderCardLayout } from './MobileOrderCard';
import { MobileOrderDetailFooter } from './MobileOrderDetailFooter';
import { MobileOrderSettingsToggles } from './MobileOrderSettingsToggles';
import { renderOrderWarningDetails } from './mobileOrderWarnings';

interface MobileOpenOrdersProps {
  defaultFilter: BlotterTableOrderFilter;
}

/**
 * Mobile counterpart to desktop `OpenOrders`. Reuses the shared
 * `useOrders().recentOrders` observable rather than opening a new WebSocket
 * subscription per tab — important on mobile for battery and data usage, and
 * for instant first paint (the cache is already populated by the provider).
 *
 * Status/ID filters are intentionally disabled here: the Open tab is defined
 * by the client-side `isOrderComplete` predicate.
 *
 * Row taps open a {@link MobileRowDetailSheet} via the default
 * {@link MobileDataTable} behaviour — no explicit `onRowPress` needed.
 */
export function MobileOpenOrders({ defaultFilter }: MobileOpenOrdersProps) {
  const { dataObservable: liveOrders, errorObservable: recentOrdersError } = useOpenOrdersBlotterObs();
  const predictionColumns = usePredictionMarketBlotterColumns();
  const columns = useOrderColumns({ additionalColumns: predictionColumns });

  const persisted = usePersistedBlotterTable(BLOTTER_ID_OPEN_ORDERS, { filter: defaultFilter });

  const filteredOrders = useOrderFilter({
    initialFilter: persisted.initialState?.filter,
    saveFilter: persisted.onFilterChanged,
    includeStatusFilter: false,
    includeIDFilter: false,
  });

  const blotterTable = useBlotterTable<Order>({
    dataObservable: liveOrders,
    errorObservable: recentOrdersError,
    rowID: 'OrderID',
    density: BlotterDensity.Compact,
    clientLocalFilter: filteredOrders.clientSideFilter,
    columns,
    persistence: persisted,
    exportOptions: { name: 'OpenOrders' },
  });

  const filterBuilderAccordion = useAccordionFilterBuilder({
    filterBuilderProps: filteredOrders.filterBuilderProps,
  });

  const rowDetailFooter = useCallback((data: Order) => <MobileOrderDetailFooter order={data} />, []);
  const { cardHeight, cardRendererProps } = useMobileOrderCardLayout();

  return (
    <>
      <MobileBlotterToolbar
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        showDateRangePicker={false}
        settingsContent={
          <MobileOrderSettingsToggles
            filter={filteredOrders.filter}
            changeFilter={filteredOrders.changeFilter}
            showAPIOrdersToggle={false}
          />
        }
      />
      <MobileDataTable<Order>
        {...blotterTable}
        cardRenderer={MobileOrderCard}
        cardRendererProps={cardRendererProps}
        cardHeight={cardHeight}
        detailsTitle="Order Details"
        rowDetailFooter={rowDetailFooter}
        rowDetailHeader={renderOrderWarningDetails}
      />
    </>
  );
}
